import { useMemo, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import { BellRing, CheckCheck, ChevronDown, Download, FileQuestion, FileWarning, ListTodo, Loader2, Paperclip, Sparkles, UserCheck, UserPlus, Users } from 'lucide-react';
import type { Address, AttachmentRequest, AttachmentView, Mailbox } from '@mailpilot/shared';
import { api, downloadFile } from '../../lib/api';
import { qk } from '../../lib/keys';
import { bytes, displayName, fileKind } from '../../lib/format';
import type { ThreadDetail } from '../../hooks/useThreads';
import { useBlobUrl } from '../../hooks/useBlobUrl';
import { useAcknowledge, useAttentionItems } from '../../hooks/useAttention';
import { Avatar } from '../ui/Avatar';
import { Button } from '../ui/Button';
import { FolderCard } from '../ui/Vivid';
import { useToast } from '../ui/Toast';
import { attachmentStatusLabel, fileIcon } from './AttachmentChip';
import { ResolveRequestDialog } from '../library/ResolveRequestDialog';

/** Compact section for the ~340px details sheet: micro uppercase label with icon and count, no card chrome. */
function Section({ title, icon, children, count, tone }: { title: string; icon: ReactNode; children: ReactNode; count?: number; tone?: 'danger' | 'warn' }) {
  const folder = tone === 'danger' ? 'salmon' : tone === 'warn' ? 'yellow' : 'surface';
  return (
    <div className="px-4 pt-4 last:pb-2">
      <FolderCard tone={folder} title={title} icon={icon} actions={count !== undefined && count > 0 ? <span className="mp-pill mp-pill-neutral !h-5">{count}</span> : undefined}>
        {children}
      </FolderCard>
    </div>
  );
}

function Thumb({ a }: { a: AttachmentView }) {
  const { url, error } = useBlobUrl(a.previewKind === 'image' ? `/api/attachments/${a.id}/preview` : null);
  const toast = useToast();
  return (
    <button
      type="button"
      onClick={() => downloadFile(`/api/attachments/${a.id}/download`, a.filename).catch((e) => toast.error(e))}
      className="group relative aspect-square overflow-hidden rounded-xl border border-hairline bg-sunken"
      title={a.filename}
      aria-label={`Download ${a.filename}`}
    >
      {url && !error ? <img src={url} alt="" className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105" /> : <span className="flex h-full items-center justify-center text-muted">{fileIcon(a.mimeType, 'h-5 w-5')}</span>}
    </button>
  );
}

function AttachmentRow({ a }: { a: AttachmentView }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const st = attachmentStatusLabel[a.status];
  const summarise = useMutation({
    mutationFn: () => api('POST /api/attachments/:id/summarise', { params: { id: a.id } }),
    onSuccess: () => {
      setOpen(true);
      void qc.invalidateQueries({ queryKey: ['thread'] });
    },
    onError: (e) => toast.error(e),
  });
  const s = a.summary;
  const limited = ['partial', 'unsupported', 'encrypted', 'unreadable', 'too_large', 'failed'].includes(a.status);
  const busy = a.status === 'parsing' || a.status === 'pending';
  const canSummarise = a.status !== 'parsing' && a.status !== 'pending' && a.status !== 'encrypted' && a.status !== 'unsupported';
  return (
    <li className="group rounded-xl transition-colors hover:bg-raised">
      <div className="flex items-center gap-3 px-2 py-2">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] bg-primary-soft text-primary-ink">{fileIcon(a.mimeType)}</span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-[0.8125rem] font-medium leading-snug text-ink" title={a.filename}>{a.filename}</p>
          <p className="truncate text-[0.6875rem] text-muted">
            {fileKind(a.mimeType, a.filename)} · {bytes(a.size)} ·{' '}
            <span className={clsx(st.tone === 'bad' && 'text-danger-ink', st.tone === 'warn' && 'text-warn-ink')}>{st.label}</span>
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-0.5">
          {busy ? (
            <Loader2 className="h-4 w-4 animate-spin text-muted" aria-label="Processing" />
          ) : (
            canSummarise && (
              <button
                type="button"
                onClick={() => summarise.mutate()}
                disabled={summarise.isPending}
                aria-label={s ? `Refresh summary of ${a.filename}` : `Summarise ${a.filename}`}
                title={s ? 'Refresh summary' : 'Summarise with AI'}
                className={clsx('inline-flex h-8 w-8 items-center justify-center rounded-lg transition-all hover:bg-primary-soft hover:text-primary-ink', s ? 'text-muted opacity-0 group-hover:opacity-100 group-focus-within:opacity-100' : 'text-primary-ink')}
              >
                {summarise.isPending ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <Sparkles className="h-4 w-4" aria-hidden />}
              </button>
            )
          )}
          <button
            type="button"
            aria-label={`Download ${a.filename}`}
            title="Download"
            onClick={() => downloadFile(`/api/attachments/${a.id}/download`, a.filename).catch((e) => toast.error(e))}
            className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-muted opacity-0 transition-all hover:bg-surface hover:text-ink group-hover:opacity-100 group-focus-within:opacity-100"
          >
            <Download className="h-4 w-4" aria-hidden />
          </button>
        </div>
      </div>
      {limited && (
        <p className="mx-2 mb-2 flex items-start gap-1.5 rounded-lg bg-warn-soft px-2.5 py-1.5 text-[0.6875rem] leading-snug text-warn-ink">
          <FileWarning className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden />
          {a.statusDetail ?? `${st.label}. Any summary may be incomplete.`}
        </p>
      )}
      {s && (
        <div className="px-2 pb-2">
          <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open} className="inline-flex items-center gap-1 rounded-md px-1 text-[0.6875rem] font-semibold text-primary-ink hover:bg-primary-soft">
            <ChevronDown className={clsx('h-3.5 w-3.5 transition-transform', open && 'rotate-180')} aria-hidden /> {open ? 'Hide summary' : 'Summary'}
          </button>
          {open && (
            <div className="mt-2 space-y-2.5 rounded-xl bg-surface px-3 py-2.5 text-xs shadow-sm">
              {s.keyPoints.length > 0 && (
                <ul className="list-disc space-y-1 pl-4 leading-relaxed text-ink">
                  {s.keyPoints.map((k, i) => <li key={i}>{k}</li>)}
                </ul>
              )}
              {s.dates.length > 0 && (
                <div>
                  <p className="mb-1 text-[0.625rem] font-semibold uppercase tracking-wider text-subtle">Dates</p>
                  <ul className="space-y-0.5">
                    {s.dates.map((d, i) => (
                      <li key={i} className="text-ink"><b>{d.value}</b> — {d.context} {d.ref && <span className="text-muted">({d.ref})</span>}</li>
                    ))}
                  </ul>
                </div>
              )}
              {s.amounts.length > 0 && (
                <div>
                  <p className="mb-1 text-[0.625rem] font-semibold uppercase tracking-wider text-subtle">Amounts</p>
                  <ul className="space-y-0.5">
                    {s.amounts.map((d, i) => (
                      <li key={i} className="text-ink"><b>{d.value}</b> — {d.context} {d.ref && <span className="text-muted">({d.ref})</span>}</li>
                    ))}
                  </ul>
                </div>
              )}
              <p className="text-[0.6875rem] text-muted">
                {s.pages != null && `${s.pages} page${s.pages === 1 ? '' : 's'} · `}
                {s.sheets?.length ? `Sheets: ${s.sheets.join(', ')} · ` : ''}
                {s.generatedBy ? `By ${s.generatedBy}` : ''}
              </p>
              {s.limitations.length > 0 && (
                <div className="rounded-lg bg-warn-soft px-2 py-1.5 text-warn-ink">
                  <p className="font-semibold">Limitations</p>
                  <ul className="list-disc pl-4">{s.limitations.map((l, i) => <li key={i}>{l}</li>)}</ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </li>
  );
}

function Participant({ addr, savedEmails, onSave, saving }: { addr: Address; savedEmails: Map<string, { id: string; name: string; company: string | null }>; onSave: (a: Address) => void; saving: boolean }) {
  const saved = savedEmails.get(addr.email.toLowerCase());
  return (
    <li className="group flex items-center gap-3 rounded-xl px-2 py-1.5 transition-colors hover:bg-raised">
      <Avatar name={addr.name} email={addr.email} size={32} />
      <div className="min-w-0 flex-1">
        <p className="truncate text-[0.8125rem] font-medium leading-snug text-ink">{saved?.name ?? displayName(addr)}</p>
        <p className="truncate text-[0.6875rem] text-muted">{addr.email}{saved?.company ? ` · ${saved.company}` : ''}</p>
      </div>
      {saved ? (
        <Link to="/contacts" title="Saved contact — open contacts" className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-success-ink transition-colors hover:bg-success-soft" aria-label={`${addr.email} is a saved contact`}>
          <UserCheck className="h-4 w-4" aria-hidden />
        </Link>
      ) : (
        <button
          type="button"
          disabled={saving}
          onClick={() => onSave(addr)}
          className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-primary-ink opacity-0 transition-all hover:bg-primary-soft group-hover:opacity-100 group-focus-within:opacity-100 disabled:opacity-50"
          aria-label={`Save ${addr.email} as a contact`}
          title="Save contact"
        >
          <UserPlus className="h-4 w-4" aria-hidden />
        </button>
      )}
    </li>
  );
}

function Checklist({ items, tone, label }: { items: { text: string; basis?: 'observed' | 'inferred' }[]; tone: 'warn' | 'primary'; label: string }) {
  return (
    <ul className="space-y-0.5" aria-label={label}>
      {items.map((it, i) => (
        <li key={i} className="flex items-start gap-2.5 rounded-lg px-2 py-1.5 text-xs leading-relaxed text-ink">
          <span className={clsx('mt-[3px] h-[14px] w-[14px] shrink-0 rounded-[4px] border-[1.5px] bg-surface', tone === 'warn' ? 'border-warn/60' : 'border-primary/50')} aria-hidden />
          <span className="min-w-0 flex-1">{it.text}</span>
          {it.basis && (
            <span className={clsx('mt-0.5 inline-flex shrink-0 items-center gap-1 text-[0.625rem] font-medium', it.basis === 'observed' ? 'text-success-ink' : 'text-info-ink')} title={it.basis === 'observed' ? 'Stated directly in a message' : 'Suggested by MailPilot'}>
              <span className={clsx('h-1.5 w-1.5 rounded-full', it.basis === 'observed' ? 'bg-success' : 'bg-info')} aria-hidden />
              {it.basis === 'observed' ? 'Observed' : 'Inferred'}
            </span>
          )}
        </li>
      ))}
    </ul>
  );
}

export function ContextPanel({ detail, mailboxes }: { detail: ThreadDetail; mailboxes: Mailbox[] }) {
  const qc = useQueryClient();
  const toast = useToast();
  const { thread, messages, analysis } = detail;
  const [resolving, setResolving] = useState<AttachmentRequest | null>(null);
  const ack = useAcknowledge();
  const attention = useAttentionItems('open');
  const threadAttention = (attention.data?.items ?? []).filter((i) => i.threadId === thread.id);
  const requests = useQuery({ queryKey: qk.attachmentRequests('open'), queryFn: () => api('GET /api/attachment-requests', { query: { status: 'open' } }) });
  const threadRequests = (requests.data?.requests ?? []).filter((r) => r.threadId === thread.id);

  const attachments = messages.flatMap((m) => m.attachments);
  const images = attachments.filter((a) => a.previewKind === 'image');
  const myEmails = new Set(mailboxes.map((m) => m.email.toLowerCase()));
  const participants = useMemo(() => {
    const seen = new Map<string, Address>();
    for (const m of messages) for (const a of [m.from, ...m.to, ...m.cc]) if (!myEmails.has(a.email.toLowerCase()) && !seen.has(a.email.toLowerCase())) seen.set(a.email.toLowerCase(), a);
    if (seen.size === 0) thread.participants.forEach((a) => !myEmails.has(a.email.toLowerCase()) && seen.set(a.email.toLowerCase(), a));
    return Array.from(seen.values());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, thread.participants, mailboxes]);

  const contacts = useQuery({ queryKey: qk.contacts({}), queryFn: () => api('GET /api/contacts', { query: {} }), staleTime: 120_000 });
  const savedEmails = useMemo(() => {
    const m = new Map<string, { id: string; name: string; company: string | null }>();
    contacts.data?.contacts.forEach((c) => c.emails.forEach((e) => m.set(e.toLowerCase(), { id: c.id, name: c.name, company: c.company })));
    return m;
  }, [contacts.data]);
  const saveContact = useMutation({
    mutationFn: (a: Address) => api('POST /api/contacts', { body: { name: a.name ?? a.email, emails: [a.email], source: 'conversation' } }),
    onSuccess: (r) => {
      toast.success(`Saved ${r.contact.name} to contacts.`);
      void qc.invalidateQueries({ queryKey: qk.contactsRoot });
    },
    onError: (e) => toast.error(e),
  });

  const outstanding = (analysis?.findings ?? []).filter((f) => f.owner !== 'them' && ['unanswered_question', 'requested_information', 'missing_document', 'pending_approval', 'deadline', 'promise'].includes(f.kind));

  return (
    <div className="pb-6">
      {threadAttention.length > 0 && (
        <Section title="Reminders" icon={<BellRing />} tone="danger" count={threadAttention.length}>
          <ul className="space-y-2">
            {threadAttention.map((it) => (
              <li key={it.id} className="rounded-xl border border-danger/20 bg-danger-soft/70 p-3">
                <p className="text-[0.8125rem] font-semibold leading-snug text-danger-ink">{it.title}</p>
                <p className="mt-1 text-xs leading-relaxed text-danger-ink/90">{it.body}</p>
                <Button size="sm" variant="primary" className="mt-2.5" icon={<CheckCheck className="h-4 w-4" />} loading={ack.isPending && ack.variables === it.id} onClick={() => ack.mutate(it.id)}>
                  Seen / Acknowledged
                </Button>
                <p className="mt-2 text-[0.6875rem] text-danger-ink/70">Opening this email does not acknowledge it.</p>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {threadRequests.length > 0 && (
        <Section title="Files requested" icon={<FileQuestion />} tone="warn" count={threadRequests.length}>
          <ul className="space-y-2">
            {threadRequests.map((r) => (
              <li key={r.id} className="rounded-xl border border-warn/25 bg-warn-soft/70 p-3">
                <p className="text-[0.8125rem] font-semibold leading-snug text-warn-ink">{r.description}</p>
                <p className="mt-1 text-xs leading-relaxed text-warn-ink">{r.reason}</p>
                {r.requestedBy && <p className="mt-1 text-[0.6875rem] text-warn-ink/80">Asked by {displayName(r.requestedBy)}</p>}
                <Button size="sm" variant="primary" className="mt-2.5" onClick={() => setResolving(r)}>
                  Select or upload file
                </Button>
              </li>
            ))}
          </ul>
        </Section>
      )}

      <Section title="Attachments" icon={<Paperclip />} count={attachments.length}>
        {attachments.length === 0 ? (
          <p className="px-2 text-xs text-muted">No attachments in this conversation.</p>
        ) : (
          <div className="space-y-3">
            {images.length > 0 && (
              <div className="grid grid-cols-3 gap-2">
                {images.slice(0, 6).map((a) => <Thumb key={a.id} a={a} />)}
              </div>
            )}
            <ul className="-mx-2 space-y-0.5">
              {attachments.map((a) => <AttachmentRow key={a.id} a={a} />)}
            </ul>
          </div>
        )}
      </Section>

      <Section title="People" icon={<Users />} count={participants.length}>
        <ul className="-mx-2 space-y-0.5">
          {participants.map((p) => (
            <Participant key={p.email} addr={p} savedEmails={savedEmails} onSave={(a) => saveContact.mutate(a)} saving={saveContact.isPending} />
          ))}
        </ul>
      </Section>

      <Section title="Outstanding & next actions" icon={<ListTodo />} count={analysis ? outstanding.length + analysis.nextActions.length : undefined}>
        {!analysis ? (
          <p className="px-2 text-xs leading-relaxed text-muted">Analyse the conversation to see open questions and next actions.</p>
        ) : outstanding.length === 0 && analysis.nextActions.length === 0 ? (
          <p className="px-2 text-xs leading-relaxed text-muted">Nothing outstanding found in the analysed messages.</p>
        ) : (
          <div className="-mx-2 space-y-3">
            {outstanding.length > 0 && <Checklist label="Outstanding items" tone="warn" items={outstanding.map((f) => ({ text: f.text, basis: f.basis }))} />}
            {analysis.nextActions.length > 0 && (
              <div>
                <p className="mb-1 px-2 text-[0.625rem] font-semibold uppercase tracking-wider text-subtle">Suggested next actions</p>
                <Checklist label="Suggested next actions" tone="primary" items={analysis.nextActions.map((n) => ({ text: n }))} />
              </div>
            )}
          </div>
        )}
      </Section>
      <ResolveRequestDialog request={resolving} open={!!resolving} onClose={() => setResolving(null)} />
    </div>
  );
}
